import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

export const Landing: React.FC = () => {
  usePageTitle('InsightStudio Labs - Research → Personas → Creative Testing');
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="/logo.png"
                alt="InsightStudio Labs"
                className="h-10 w-auto"
              />
            </div>

            {/* Sign In Button */}
            <button
              onClick={() => navigate('/signin')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-500 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Research → Personas → Creative Testing
            </h1>
            <p className="text-xl sm:text-2xl text-blue-50 mb-8 max-w-4xl mx-auto leading-relaxed">
              InsightStudio Labs automates deep market research, builds realistic personas, then runs synthetic focus groups and interviews—so your creative always ships with audience validation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/signup')}
                className="px-8 py-4 bg-white text-primary-500 rounded-lg font-semibold text-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-500 transition-all shadow-lg"
              >
                Start with your brief
              </button>
              <button
                onClick={scrollToHowItWorks}
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-500 transition-all"
              >
                See a demo workflow
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20" style={{ backgroundColor: '#EFE4D2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From market intelligence to audience-validated creative in three automated steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                  1
                </div>
                <div className="mb-4 text-5xl">🔍</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  Deep Research
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Upload your brief. InsightStudio Labs scrapes and synthesizes competitive analysis, market trends, brand positioning, and audience signals into a research foundation.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                  2
                </div>
                <div className="mb-4 text-5xl">👥</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  Generate Personas
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  AI builds 5-10 detailed personas representing your target segments—complete with motivations, pain points, media habits, and how they talk about your category.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                  3
                </div>
                <div className="mb-4 text-5xl">🎯</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  Test Your Creative
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Run moderated focus groups (6-8 personas) or 1:1 interviews with your concepts, scripts, visuals, or landing pages. Probe reactions, explore objections, iterate live.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Value Prop 1 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary-500 transition-colors">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                End-to-end automation
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Skip the research agency. InsightStudio Labs handles competitive intelligence, persona creation, and audience testing in one platform.
              </p>
            </div>

            {/* Value Prop 2 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary-500 transition-colors">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Personas grounded in real signals
              </h3>
              <p className="text-gray-600 leading-relaxed">
                No generic archetypes. Personas are built directly from fresh market/competitive research specific to your brief and category.
              </p>
            </div>

            {/* Value Prop 3 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary-500 transition-colors">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Two testing modes for every need
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Focus Groups (group dynamics, debate, consensus) and 1:1 Interviews (deep exploration, individual motivations) give you flexibility for every research question.
              </p>
            </div>

            {/* Value Prop 4 */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary-500 transition-colors">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Creative-first interface
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Upload storyboards, wireframes, copy sets, video cuts. Get feedback formatted for creative teams—not research departments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sample Workflows Section */}
      <section className="py-20" style={{ backgroundColor: '#EFE4D2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Sample Workflows
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real examples of how creative teams use InsightStudio Labs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Workflow 1 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Campaign Territory Testing
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Research</div>
                  <div className="text-gray-700">Competitive positioning + category trends</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Personas</div>
                  <div className="text-gray-700">Primary + stretch targets</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Testing</div>
                  <div className="text-gray-700">2 focus groups comparing 3 territory directions</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Output</div>
                  <div className="text-gray-700">Clear winner + supporting quotes</div>
                </div>
              </div>
            </div>

            {/* Workflow 2 */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Execution Testing
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Research</div>
                  <div className="text-gray-700">Your brand + top 3 competitors' recent work</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Personas</div>
                  <div className="text-gray-700">3 core segments</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Testing</div>
                  <div className="text-gray-700">1:1 interviews with 3 headline/visual combinations</div>
                </div>
                <div>
                  <div className="text-sm font-semibold text-primary-500 mb-1">Output</div>
                  <div className="text-gray-700">Performance rankings + specific line edits</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-500 to-secondary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Your brief → audience-validated creative
          </h2>
          <p className="text-xl text-blue-50 mb-8 leading-relaxed">
            InsightStudio Labs gives creative teams what they've always wanted: deep market intelligence without research budgets, realistic personas without recruiting, and fast creative feedback without scheduling.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-8 py-4 bg-white text-primary-500 rounded-lg font-semibold text-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-500 transition-all shadow-lg"
          >
            Get Started
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/logo.png"
                alt="InsightStudio Labs"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-sm">From market research to creative sign-off</p>
            <div className="mt-6 text-sm text-gray-400">
              <button
                onClick={() => navigate('/signin')}
                className="hover:text-white transition-colors"
              >
                Sign In
              </button>
              <span className="mx-3">|</span>
              <button
                onClick={() => navigate('/signup')}
                className="hover:text-white transition-colors"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
